import {
  type __InputStylesNames,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  type TextInputProps,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { ReactElement } from 'react'
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AISplitButton } from '@/components/AISplitButton'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/AIInputWrapper.css'

type AIFieldState = {
  state: 'idle' | 'loading' | 'pending'
  suggestion: string | null
}

type AIInputWrapperProps = {
  fieldState: AIFieldState
  children: ReactElement<TextInputProps>
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  onCancel?: () => void
  /** When provided, "Guide…" appears in idle dropdown; user can add instructions sent as user_instructions to the generate endpoint. */
  onAskSidekickWithGuide?: (instructions: string) => void
  disabled?: boolean
}

function useStatusDisplay(fieldState: AIFieldState) {
  return {
    statusText: fieldState.state === 'loading' ? 'Processing' : '',
  }
}

function useInputModification(
  originalProps: TextInputProps,
  fieldState: AIFieldState,
  onReject: () => void,
  onCancel?: () => void,
) {
  const { state, suggestion } = fieldState
  const isPending = state === 'pending'
  const isLoading = state === 'loading'
  const isAIActive = isPending || isLoading

  // On pending → idle transition, briefly force controlled mode so the DOM
  // shows the restored value (defaultValue alone won't update a mounted input).
  const prevStateRef = useRef(state)
  const [forceControlledValue, setForceControlledValue] = useState<string | null>(null)

  if (prevStateRef.current === 'pending' && state === 'idle') {
    const restoredValue = originalProps.defaultValue ?? originalProps.value ?? ''
    if (forceControlledValue === null) {
      setForceControlledValue(String(restoredValue))
    }
  }
  prevStateRef.current = state

  useEffect(() => {
    if (forceControlledValue !== null) setForceControlledValue(null)
  }, [forceControlledValue])

  return useMemo(() => {
    const classNames = originalProps.classNames as Partial<Record<__InputStylesNames, string>>

    const valueProps: Pick<TextInputProps, 'value' | 'defaultValue'> = isPending
      ? { value: suggestion ?? '', defaultValue: undefined }
      : forceControlledValue !== null
        ? { value: forceControlledValue, defaultValue: undefined }
        : originalProps.value !== undefined
          ? { value: originalProps.value }
          : {}

    return {
      ...originalProps,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isPending) onReject()
        if (isLoading) onCancel?.()
        // Clear force-controlled so the keystroke takes effect in uncontrolled mode
        if (forceControlledValue !== null) {
          setForceControlledValue(null)
        }
        originalProps.onChange?.(e)
      },
      ...valueProps,
      withAsterisk: false,
      variant: isAIActive ? 'filled' : originalProps.variant,
      classNames: {
        ...originalProps.classNames,
        input: theme.cx(classNames?.input, isAIActive && classes.input),
      },
      error: originalProps.error,
    }
  }, [
    originalProps,
    fieldState,
    onReject,
    onCancel,
    forceControlledValue,
    isPending,
    isLoading,
    suggestion,
    isAIActive,
  ])
}

// Guide modal content: instructions textarea + Generate / Cancel (exported for reuse in FAQEntry)
export function GuideModalContent({
  instructions,
  onInstructionsChange,
  onGenerate,
  onClose,
  disabled,
}: {
  instructions: string
  onInstructionsChange: (value: string) => void
  onGenerate: () => void
  onClose: () => void
  disabled?: boolean
}) {
  const handleGenerate = useCallback(() => {
    onGenerate()
    onClose()
  }, [onGenerate, onClose])

  return (
    <Stack gap="md">
      <Textarea
        label="Instructions for Sidekick"
        placeholder="e.g. Use a professional tone"
        value={instructions}
        onChange={e => onInstructionsChange(e.target.value)}
        autosize
        minRows={3}
        maxRows={6}
        disabled={disabled}
      />
      <Group gap="sm" justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleGenerate} disabled={disabled}>
          Generate
        </Button>
      </Group>
    </Stack>
  )
}

// Action buttons component
function ActionButtons({
  fieldState,
  onAskSidekick,
  onAccept,
  onReject,
  onRegenerate,
  onCancel,
  onAskSidekickWithGuide,
  disabled,
  statusText,
}: {
  fieldState: AIFieldState
  onAskSidekick: () => void
  onAccept: () => void
  onReject: () => void
  onRegenerate?: () => void
  onCancel?: () => void
  onAskSidekickWithGuide?: (instructions: string) => void
  disabled?: boolean
  statusText: string
}) {
  const [guideOpen, setGuideOpen] = useState(false)
  const [guideInstructions, setGuideInstructions] = useState('')
  const isNarrowViewport = !useMediaQuery(`(min-width: ${theme.breakpoints.sm})`)

  const handleGuideGenerate = useCallback(() => {
    if (onAskSidekickWithGuide) {
      onAskSidekickWithGuide(guideInstructions.trim())
      setGuideInstructions('')
    }
  }, [onAskSidekickWithGuide, guideInstructions])

  const handleGuideClose = useCallback(() => {
    setGuideOpen(false)
    setGuideInstructions('')
  }, [])

  const buttons = (
    <AISplitButton
      fieldState={fieldState.state}
      onAskSidekick={onAskSidekick}
      onAccept={onAccept}
      onReject={onReject}
      onRegenerate={onRegenerate}
      onCancel={onCancel}
      onGuideClick={onAskSidekickWithGuide ? () => setGuideOpen(true) : undefined}
      disabled={disabled}
    />
  )

  const isLoading = fieldState.state === 'loading'

  const statusDisplay = statusText ? (
    <Group gap={4} align="center" wrap="nowrap">
      <Text fw={600} tt="uppercase" size="xs" variant="gradient">
        {statusText}
      </Text>
      {isLoading && <Loader size={12} />}
    </Group>
  ) : null

  if (!onAskSidekickWithGuide) {
    return (
      <Group align="center" gap="xs">
        {statusDisplay}
        {buttons}
      </Group>
    )
  }

  return (
    <Group align="center" gap="xs">
      {statusDisplay}
      {buttons}
      <Modal
        title="Guide Sidekick"
        opened={guideOpen}
        onClose={handleGuideClose}
        centered
        size="sm"
        fullScreen={isNarrowViewport}>
        <GuideModalContent
          instructions={guideInstructions}
          onInstructionsChange={setGuideInstructions}
          onGenerate={handleGuideGenerate}
          onClose={handleGuideClose}
          disabled={disabled}
        />
      </Modal>
    </Group>
  )
}

export function AIInputWrapper(props: AIInputWrapperProps) {
  const {
    fieldState,
    onAskSidekick,
    onAccept,
    onReject,
    onRegenerate,
    onCancel,
    onAskSidekickWithGuide,
    disabled,
    children,
  } = props

  const statusDisplay = useStatusDisplay(fieldState)
  const modifiedInputProps = useInputModification(children.props, fieldState, onReject, onCancel)

  if (disabled) return children

  const enhancedProps: TextInputProps = {
    ...modifiedInputProps,
    label: (
      <Group justify="space-between" align="center">
        <InputLabel label={children.props.label} withAsterisk={children.props.withAsterisk} withSpacer={false} />

        <ActionButtons
          fieldState={fieldState}
          onAskSidekick={onAskSidekick}
          onAccept={onAccept}
          onReject={onReject}
          onRegenerate={onRegenerate}
          onCancel={onCancel}
          onAskSidekickWithGuide={onAskSidekickWithGuide}
          disabled={disabled}
          statusText={statusDisplay.statusText}
        />
      </Group>
    ),
  }

  return cloneElement(children, enhancedProps)
}
